import React, { useEffect, useState } from 'react';
import { HEADING_FONTS, type HeadingFont, type MusicTrack, type Overlay, type Scene, type VideoFormat, type VideoSpec } from '@hyvay/video-engine';
import type { VideoConfig } from '../../services/video.service';
import type { PostGroup } from './PostDetailDrawer';
import {
    ASPECTS, AspectGlyph, BEAT, Dropdown, Ico, LabeledInput, POST_BADGE, POST_STATUS, Switch, TRACKS,
    fieldLabel, fmtWhenShort, inputStyle, textareaStyle,
} from './vsUi';

type Patch = (fn: (s: VideoSpec) => VideoSpec) => void;

/**
 * Right-hand inspector of the video studio: General, the selected scene,
 * Voiceover & music, Colors & font, Posts. Collapsible like the design; every
 * edit goes through the studio's debounced patchSpec save.
 */
export const VideoInspector: React.FC<{
    spec: VideoSpec;
    cfg: VideoConfig | null;
    selected: number;
    patchSpec: Patch;
    groups: PostGroup[];
    canPost: boolean;
    onSchedule: () => void;
    onOpenPost: (g: PostGroup) => void;
    onScheduler: () => void;
}> = ({ spec, cfg, selected, patchSpec, groups, canPost, onSchedule, onOpenPost, onScheduler }) => {
    const [open, setOpen] = useState({ gen: true, scene: true, voice: true, brand: true, posts: true });
    const flip = (k: keyof typeof open) => setOpen((o) => ({ ...o, [k]: !o[k] }));
    const scene = spec.scenes[Math.min(selected, spec.scenes.length - 1)];
    const idx = Math.min(selected, spec.scenes.length - 1);
    // Captions need something to show: narration, or a clip playing its own speech.
    const canCaption = spec.voice.enabled || spec.scenes.some((x) => x.type === 'video' && x.muted === false);
    const voices = cfg?.voices ?? [];
    const font = HEADING_FONTS.find((f) => f.id === spec.theme.headingFont);
    // "Warm · Bahasa Indonesia" → the language is the last part of the voice's meta.
    const voice = voices.find((v) => v.id === spec.voice.voiceId) ?? voices[0];
    const voiceLang = voice?.meta.split('·').pop()?.trim() || 'Bahasa Indonesia';

    return (
        <div className="hv-vs-insp" style={{ position: 'relative', zIndex: 4, flex: 'none', width: 300, display: 'flex', flexDirection: 'column', minHeight: 0, margin: '8px 8px 8px 0', borderRadius: 14, overflow: 'hidden', border: '1px solid var(--hv-line)', boxShadow: '0 18px 40px -28px var(--hv-shadow),0 2px 6px -3px rgba(0,0,0,0.06)', background: 'var(--hv-page)' }}>
            <div style={{ flex: '1 1 0', minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: '12px 16px 16px' }}>
                <Section title="General" open={open.gen} onToggle={() => flip('gen')}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 6 }}>
                        <span style={fieldLabel}>Aspect ratio</span>
                        <Dropdown<VideoFormat>
                            label="Aspect ratio"
                            value={spec.format}
                            options={ASPECTS.map((a) => ({ value: a.value, label: a.label, meta: a.hint, lead: <AspectGlyph w={a.w} h={a.h} /> }))}
                            onChange={(format) => patchSpec((s) => ({ ...s, format }))}
                        />
                    </div>
                    <Switch on={!!spec.brand.showLogo} onChange={(v) => patchSpec((s) => ({ ...s, brand: { ...s.brand, showLogo: v } }))} compact label="Show logo on every scene" title={spec.brand.logoUrl ? undefined : 'Your brand initials are used until a logo is added'} />
                    <Switch on={spec.endCard?.enabled !== false} onChange={(v) => patchSpec((s) => ({ ...s, endCard: { enabled: v } }))} compact label="WhatsApp end card" />
                </Section>

                {scene && (
                    <Section
                        title={<><span style={{ fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em' }}>Scene {idx + 1}</span><span style={{ minWidth: 0, fontSize: 12.5, fontWeight: 400, color: 'var(--hv-muted)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{BEAT[scene.type]}</span></>}
                        open={open.scene}
                        onToggle={() => flip('scene')}
                    >
                        <SceneEditor key={scene.id} scene={scene} onChange={(next) => patchSpec((s) => ({ ...s, scenes: s.scenes.map((x, i) => (i === idx ? next : x)) }))} />
                    </Section>
                )}

                <Section title="Voiceover & music" open={open.voice} onToggle={() => flip('voice')} gap={6}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 8 }}>
                        <Switch
                            on={spec.voice.enabled}
                            disabled={!cfg?.voiceEnabled}
                            title={cfg?.voiceEnabled ? undefined : 'Voiceover isn\'t set up on this server yet'}
                            onChange={(v) => patchSpec((s) => ({ ...s, voice: { ...s.voice, enabled: v } }))}
                            label={`Voiceover (${voiceLang})`}
                        />
                        <Switch
                            on={!!spec.captions?.enabled && canCaption}
                            disabled={!canCaption}
                            title={canCaption ? undefined : 'Turn on the voiceover, or use a clip\'s own audio, to get captions'}
                            onChange={(v) => patchSpec((s) => ({ ...s, captions: { enabled: v } }))}
                            label="Captions"
                        />
                    </div>
                    <span style={fieldLabel}>Voiceover</span>
                    <Dropdown<string>
                        label="Voiceover"
                        value={spec.voice.voiceId ?? voices[0]?.id}
                        disabled={!voices.length}
                        placeholder={voices.length ? 'Choose a voice' : 'Default voice'}
                        options={voices.map((v) => ({ value: v.id, label: v.name, meta: v.meta }))}
                        onChange={(voiceId) => patchSpec((s) => ({ ...s, voice: { ...s.voice, voiceId } }))}
                    />
                    <span style={{ ...fieldLabel, marginTop: 8 }}>Music</span>
                    <Dropdown<MusicTrack>
                        label="Music"
                        value={spec.music.track}
                        options={TRACKS.map((t) => ({ value: t.value, label: t.label, meta: t.meta }))}
                        onChange={(track) => patchSpec((s) => ({ ...s, music: { ...s.music, track } }))}
                    />
                    {spec.music.track !== 'none' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ flex: 1, ...fieldLabel }}>Music volume</span>
                                <span style={{ fontSize: 12, color: 'var(--hv-ink)', fontVariantNumeric: 'tabular-nums' }}>{Math.round(spec.music.volume * 100)}%</span>
                            </div>
                            <input type="range" min={0} max={100} step={1} value={Math.round(spec.music.volume * 100)} aria-label="Music volume" onChange={(e) => patchSpec((s) => ({ ...s, music: { ...s.music, volume: Number(e.target.value) / 100 } }))} style={{ width: '100%', accentColor: 'var(--brand,#13422C)', cursor: 'pointer' }} />
                        </div>
                    )}
                </Section>

                <Section title="Colors & font" open={open.brand} onToggle={() => flip('brand')} gap={6}>
                    <span style={fieldLabel}>Font</span>
                    <Dropdown<HeadingFont>
                        label="Font"
                        value={spec.theme.headingFont}
                        placeholder={font?.name}
                        options={HEADING_FONTS.map((f) => ({ value: f.id, label: f.name, lead: <span style={{ flex: 'none', width: 20, fontFamily: f.family, fontWeight: f.weight, fontSize: 15, lineHeight: 1 }}>Aa</span> }))}
                        onChange={(headingFont) => patchSpec((s) => ({ ...s, theme: { ...s.theme, headingFont } }))}
                    />
                    <span style={{ ...fieldLabel, marginTop: 8 }}>Colors</span>
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 8 }}>
                        {([['primary', 'Primary'], ['accent', 'Accent'], ['background', 'Background'], ['ink', 'Text']] as const).map(([k, label]) => (
                            <label key={k} className="hv-vs-bd" style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 9, height: 44, padding: '0 10px 0 7px', borderRadius: 10, border: '1px solid var(--hv-line-2)', background: 'var(--hv-card)', cursor: 'pointer' }}>
                                <span style={{ flex: 'none', width: 28, height: 28, borderRadius: 7, background: spec.theme[k], boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.12)' }} />
                                <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 12.5, fontWeight: 600, color: 'var(--hv-ink)' }}>{label}</span>
                                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 10, color: 'var(--hv-muted)', textTransform: 'uppercase' }}>{spec.theme[k]}</span>
                                </span>
                                <input type="color" value={toHex6(spec.theme[k])} aria-label={`${label} color`} onChange={(e) => { const v = e.target.value; patchSpec((s) => ({ ...s, theme: { ...s.theme, [k]: v } })); }} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', border: 0, padding: 0 }} />
                            </label>
                        ))}
                    </div>
                </Section>

                <Section title="Posts" open={open.posts} onToggle={() => flip('posts')} gap={8} pull>
                    {groups.length === 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '8px 0 4px', textAlign: 'center' }}>
                            <span style={{ width: 36, height: 36, borderRadius: 999, background: 'var(--hv-page-2)', color: 'var(--hv-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{Ico.calendarBig()}</span>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--hv-ink)' }}>No posts yet</div>
                            <div style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--hv-body)' }}>
                                {canPost ? 'Post this video now, or pick a date and channels and it posts automatically.' : 'Render the video, then post it now or pick a date and channels and it posts automatically.'}
                            </div>
                            <button type="button" className="hv-vs-bd" disabled={!canPost} onClick={onSchedule} title={canPost ? undefined : 'Render the video first'} style={{ marginTop: 4, height: 32, padding: '0 14px', borderRadius: 999, border: '1px solid var(--hv-line-2)', background: 'var(--hv-card)', color: 'var(--hv-ink)', fontSize: 12.5, fontWeight: 600, cursor: canPost ? 'pointer' : 'not-allowed', opacity: canPost ? 1 : 0.5 }}>Schedule a post</button>
                        </div>
                    ) : (
                        <>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {groups.map((g) => (
                                    <div key={g.key} role="button" tabIndex={0} className="hv-vs-row" onClick={() => onOpenPost(g)} onKeyDown={(e) => { if (e.key === 'Enter') onOpenPost(g); }} style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 -8px', padding: '10px 8px', borderRadius: 6, cursor: 'pointer' }}>
                                        <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: 'var(--hv-ink)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fmtWhenShort(g.at)}</span>
                                        <span style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 7px', borderRadius: 999, background: POST_BADGE[g.status], color: 'var(--hv-ink)', fontSize: 11, fontWeight: 600 }}>{POST_STATUS[g.status]}</span>
                                    </div>
                                ))}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 10, marginTop: 6 }}>
                                <button type="button" className="hv-vs-ghost" disabled={!canPost} onClick={onSchedule} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, height: 32, padding: '0 13px', whiteSpace: 'nowrap', border: '1px solid var(--hv-line-2)', borderRadius: 999, background: 'transparent', color: 'var(--hv-ink)', fontSize: 12.5, fontWeight: 600, cursor: canPost ? 'pointer' : 'not-allowed', opacity: canPost ? 1 : 0.5 }}>
                                    {Ico.plus(13)}Schedule another
                                </button>
                                <button type="button" className="hv-vs-under" onClick={onScheduler} style={{ alignSelf: 'center', border: 0, background: 'transparent', padding: 0, fontSize: 12.5, color: 'var(--hv-brand-ink)', cursor: 'pointer' }}>View in Scheduler</button>
                            </div>
                        </>
                    )}
                </Section>
            </div>
        </div>
    );
};

/** `<input type=color>` only takes #rrggbb. */
function toHex6(c: string): string {
    if (/^#[0-9a-f]{6}$/i.test(c)) return c;
    if (/^#[0-9a-f]{3}$/i.test(c)) return `#${c.slice(1).split('').map((x) => x + x).join('')}`;
    return '#000000';
}

const Section: React.FC<{ title: React.ReactNode; open: boolean; onToggle: () => void; children: React.ReactNode; gap?: number; pull?: boolean }> = ({ title, open, onToggle, children, gap = 14, pull }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 2 }}>
        <button type="button" aria-expanded={open} onClick={onToggle} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', height: 36, padding: 0, border: 0, background: 'transparent', color: 'var(--hv-ink)', cursor: 'pointer', textAlign: 'left' }}>
            {Ico.chevD(15, { color: 'var(--hv-muted)', transform: `rotate(${open ? 0 : -90}deg)`, transition: 'transform 0.2s ease' })}
            <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 8, whiteSpace: 'nowrap', overflow: 'hidden', fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em' }}>{title}</span>
        </button>
        {open && <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: 14, gap, marginTop: pull ? -6 : 0 }}>{children}</div>}
    </div>
);

/* --------------------------------------------------------------- scene */

const FIELDS: Record<Scene['type'], [string, string, string][]> = {
    title: [['eyebrow', 'Small label', 'e.g. Kopi Rembuk · Bandung'], ['title', 'Title', 'e.g. Bandung mornings start here'], ['subtitle', 'Subtitle', 'e.g. Small-batch coffee, brewed with care']],
    statement: [['text', 'Statement', 'One strong sentence'], ['emphasis', 'Highlighted words', 'Words to stress']],
    screenshot: [['caption', 'Caption', 'What this page shows'], ['url', 'Address', 'e.g. hyvay.app/your-site']],
    image: [['title', 'Title', 'e.g. Iced palm sugar latte'], ['caption', 'Caption', 'e.g. Made fresh to order']],
    features: [['title', 'Title', 'e.g. Why people come back']],
    stat: [['value', 'Number', 'e.g. 20%'], ['label', 'Label', 'e.g. off this week'], ['sub', 'Detail', 'e.g. Every drink, until Sunday']],
    quote: [['quote', 'Quote', 'What the customer said'], ['author', 'Name', 'e.g. Rina'], ['role', 'Role', 'e.g. Regular since 2023']],
    cta: [['title', 'Title', 'e.g. Order on WhatsApp'], ['subtitle', 'Subtitle', 'e.g. Pickup or delivery around Bandung'], ['buttonText', 'Button', 'e.g. Order now'], ['url', 'Link', 'e.g. wa.me/yourbusiness']],
    video: [['title', 'Title', 'Optional headline over the clip'], ['caption', 'Caption', 'Optional line under it']],
};

/** Plain-field editor for the selected scene (text, duration, narration, clip extras). */
const SceneEditor: React.FC<{ scene: Scene; onChange: (s: Scene) => void }> = ({ scene, onChange }) => {
    const set = (k: string, v: unknown) => onChange({ ...scene, [k]: v } as Scene);
    const rec = scene as unknown as Record<string, unknown>;
    return (
        <>
            {FIELDS[scene.type].map(([k, label, ph]) => (
                <LabeledInput key={k} label={label} placeholder={ph} value={String(rec[k] ?? '')} onChange={(v) => set(k, v)} />
            ))}
            {scene.type === 'features' && (
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6, ...fieldLabel }}>
                    Points (one per line)
                    <textarea className="hv-vs-in" value={scene.items.join('\n')} onChange={(e) => set('items', e.target.value.split('\n'))} rows={4} style={textareaStyle} />
                </label>
            )}
            {scene.type === 'video' && (
                <>
                    <SecondsField label="Start the clip at" value={scene.trimStart ?? 0} min={0} max={3600} onCommit={(v) => set('trimStart', v)} />
                    <Switch on={scene.muted === false} onChange={(v) => set('muted', !v)} label="Use the clip's own audio (music gets quieter)" />
                    <OverlayEditor overlays={scene.overlays ?? []} onChange={(overlays) => set('overlays', overlays)} />
                    {scene.muted === false && !!scene.subtitles?.length && (
                        <SubtitleEditor subtitles={scene.subtitles} onChange={(subtitles) => set('subtitles', subtitles)} />
                    )}
                </>
            )}
            <SecondsField label="Duration" value={scene.duration} min={2} max={15} onCommit={(v) => set('duration', v)} />
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, ...fieldLabel }}>
                Narration
                <textarea className="hv-vs-in" value={scene.narration ?? ''} placeholder="What the narrator says in this scene" onChange={(e) => set('narration', e.target.value)} rows={3} style={textareaStyle} />
            </label>
        </>
    );
};

/** Text input with a "seconds" suffix; commits (clamped) on blur / Enter, arrows nudge 0.1 s. */
const SecondsField: React.FC<{ label: string; value: number; min: number; max: number; onCommit: (v: number) => void }> = ({ label, value, min, max, onCommit }) => {
    const [draft, setDraft] = useState(fmtSec(value));
    useEffect(() => setDraft(fmtSec(value)), [value]);
    const commit = (raw: string) => {
        const n = parseFloat(raw.replace(',', '.'));
        if (!Number.isFinite(n)) { setDraft(fmtSec(value)); return; }
        const v = Math.round(Math.min(max, Math.max(min, n)) * 1000) / 1000;
        setDraft(fmtSec(v));
        if (v !== value) onCommit(v);
    };
    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, ...fieldLabel }}>
            {label}
            <span className="hv-vs-in" style={{ display: 'flex', alignItems: 'center', gap: 8, height: 36, padding: '0 12px', borderRadius: 6, border: '1px solid var(--hv-line-2)', background: 'var(--hv-card)' }}>
                <input
                    type="text"
                    inputMode="decimal"
                    aria-label={`${label} in seconds`}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={(e) => commit(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                            e.preventDefault();
                            const step = e.shiftKey ? 1 : 0.1;
                            commit(String((parseFloat(draft) || value) + (e.key === 'ArrowUp' ? step : -step)));
                        }
                    }}
                    style={{ flex: 1, minWidth: 0, border: 0, outline: 'none', background: 'transparent', color: 'var(--hv-ink)', font: 'inherit', fontSize: 13, fontVariantNumeric: 'tabular-nums', padding: 0 }}
                />
                <span style={{ flex: 'none', fontSize: 12.5, color: 'var(--hv-muted)' }}>seconds</span>
            </span>
        </label>
    );
};
const fmtSec = (v: number) => (Number.isInteger(v) ? v.toFixed(1) : String(+v.toFixed(3)));

const OVERLAY_STYLE: { value: Overlay['style']; label: string }[] = [
    { value: 'card', label: 'Card' },
    { value: 'pip', label: 'Floating' },
    { value: 'full', label: 'Full screen' },
];

/** Product pictures shown over the clip: when, how long, which style, text. Position is automatic. */
const OverlayEditor: React.FC<{ overlays: Overlay[]; onChange: (o: Overlay[]) => void }> = ({ overlays, onChange }) => {
    const set = (i: number, patch: Partial<Overlay>) => onChange(overlays.map((o, j) => (j === i ? { ...o, ...patch } : o)));
    const small: React.CSSProperties = { ...inputStyle, height: 32, padding: '0 9px', fontSize: 12.5 };
    if (!overlays.length) return <div style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--hv-muted)' }}>No products over this clip. Ask in the chat, e.g. "show the product photo while I hold it".</div>;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={fieldLabel}>Products over the clip</span>
            {overlays.map((o, i) => (
                <div key={i} style={{ padding: 10, borderRadius: 8, border: '1px solid var(--hv-line)', background: 'var(--hv-card)', display: 'grid', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 'none', width: 32, height: 32, borderRadius: 6, background: `center/contain no-repeat url("${o.imageUrl}"), var(--hv-page-2)` }} />
                        <select className="hv-vs-in" value={o.style} onChange={(e) => set(i, { style: e.target.value as Overlay['style'] })} style={{ ...small, flex: 1 }}>
                            {OVERLAY_STYLE.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
                        </select>
                        <button type="button" title="Remove" aria-label="Remove product" onClick={() => onChange(overlays.filter((_, j) => j !== i))} className="hv-vs-ghost" style={{ flex: 'none', width: 28, height: 28, border: 0, borderRadius: 6, background: 'transparent', color: 'var(--hv-danger)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{Ico.x(11)}</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                        <label style={{ display: 'grid', gap: 4, fontSize: 11.5, color: 'var(--hv-muted)' }}>Appears at (s)
                            <input className="hv-vs-in" type="number" min={0} step={0.5} value={o.start} onChange={(e) => set(i, { start: Math.max(0, parseFloat(e.target.value) || 0) })} style={small} />
                        </label>
                        <label style={{ display: 'grid', gap: 4, fontSize: 11.5, color: 'var(--hv-muted)' }}>Shows for (s)
                            <input className="hv-vs-in" type="number" min={1} step={0.5} value={o.duration} onChange={(e) => set(i, { duration: Math.max(1, parseFloat(e.target.value) || 1) })} style={small} />
                        </label>
                    </div>
                    {o.style !== 'pip' && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                            <input className="hv-vs-in" placeholder="Product name" value={o.title ?? ''} onChange={(e) => set(i, { title: e.target.value })} style={small} />
                            <input className="hv-vs-in" placeholder="Price / offer" value={o.price ?? ''} onChange={(e) => set(i, { price: e.target.value })} style={small} />
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

/** Correct the automatic transcript (e.g. a misheard brand name). Times stay as transcribed. */
const SubtitleEditor: React.FC<{ subtitles: { start: number; end: number; text: string }[]; onChange: (s: { start: number; end: number; text: string }[]) => void }> = ({ subtitles, onChange }) => {
    const [open, setOpen] = useState(false);
    const fmt = (t: number) => `${Math.floor(t / 60)}:${(t % 60).toFixed(1).padStart(4, '0')}`;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)} style={{ display: 'flex', alignItems: 'center', gap: 6, border: 0, background: 'transparent', padding: 0, cursor: 'pointer', ...fieldLabel, textAlign: 'left' }}>
                {Ico.chevR(12, { transform: `rotate(${open ? 90 : 0}deg)`, transition: 'transform 0.2s ease' })}
                Spoken captions ({subtitles.length}) · fix any mistakes
            </button>
            {open && (
                <div style={{ display: 'grid', gap: 6, maxHeight: 320, overflow: 'auto' }}>
                    {subtitles.map((x, i) => (
                        <label key={i} style={{ display: 'grid', gridTemplateColumns: '44px 1fr', gap: 6, alignItems: 'center', fontSize: 11, color: 'var(--hv-muted)', fontVariantNumeric: 'tabular-nums' }}>
                            {fmt(x.start)}
                            <input className="hv-vs-in" value={x.text} onChange={(e) => onChange(subtitles.map((y, j) => (j === i ? { ...y, text: e.target.value } : y)))} style={{ ...inputStyle, height: 30, padding: '0 8px', fontSize: 12.5 }} />
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
};
